import { useState } from 'react';

const DESIGNATIONS = ['', 'CRA', 'P.App.', 'AACI', 'Candidate', 'Other'];
const PROVINCES    = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];
const PROF_ASSIST  = [
  'N/A',
  'AIC Candidate Member (name)',
  'AOLS Member (name)',
  'OGCA Member (name)',
  'ATEFQ Member (name)',
];

export default function ClientTab({ parties, onPartiesChange }) {
  const [cosignerOpen, setCosignerOpen] = useState(parties.cosigner?.enabled ?? false);

  const setClient    = (k, v) => onPartiesChange({ ...parties, client:    { ...parties.client,    [k]: v } });
  const setAppraiser = (k, v) => onPartiesChange({ ...parties, appraiser: { ...parties.appraiser, [k]: v } });
  const setCosigner  = (k, v) => onPartiesChange({ ...parties, cosigner:  { ...parties.cosigner,  [k]: v } });

  const toggleCosigner = (checked) => {
    setCosignerOpen(checked);
    onPartiesChange({ ...parties, cosigner: { ...parties.cosigner, enabled: checked } });
  };

  const { client, appraiser, cosigner } = parties;

  return (
    <div className="rt-body">

      {/* ── TWO-COLUMN: CLIENT + APPRAISER ── */}
      <div className="rt-section">
        <div className="rt-two-col">

          {/* LEFT: CLIENT */}
          <div>
            <div className="rt-col-title">Client / Lender</div>
            <p className="rt-hint" style={{ marginBottom: 14 }}>
              Auto-filled from the workfile orderer — override if needed.
            </p>

            {[
              ['name',      'Client / Company Name', true],
              ['attention', 'Attention'],
              ['address',   'Address'],
              ['email',     'Email'],
              ['phone',     'Phone'],
              ['fax',       'Fax'],
            ].map(([key, label, required]) => (
              <div className="rt-field" key={key} style={{ marginBottom: 10 }}>
                <label>
                  {label}
                  {required && <span className="rt-required"> *</span>}
                </label>
                <input
                  type={key === 'email' ? 'email' : 'text'}
                  value={client[key] ?? ''}
                  onChange={e => setClient(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* RIGHT: APPRAISER */}
          <div>
            <div className="rt-col-title">Appraiser</div>
            <p className="rt-hint" style={{ marginBottom: 14 }}>
              Auto-filled from your profile — update in Account Settings.
            </p>

            {[
              ['name',    'AIC Member Name', true],
              ['company', 'Company / Firm'],
              ['address', 'Address'],
              ['email',   'Email'],
              ['phone',   'Phone'],
              ['fax',     'Fax'],
            ].map(([key, label, required]) => (
              <div className="rt-field" key={key} style={{ marginBottom: 10 }}>
                <label>
                  {label}
                  {required && <span className="rt-required"> *</span>}
                </label>
                <input
                  type={key === 'email' ? 'email' : 'text'}
                  value={appraiser[key] ?? ''}
                  onChange={e => setAppraiser(key, e.target.value)}
                />
              </div>
            ))}

            <div className="rt-row" style={{ marginTop: 2 }}>
              <div className="rt-field">
                <label>AIC Membership #</label>
                <input type="text" value={appraiser.membership_number ?? ''}
                  onChange={e => setAppraiser('membership_number', e.target.value)} />
              </div>
              <div className="rt-field">
                <label>Province</label>
                <select value={appraiser.province ?? ''}
                  onChange={e => setAppraiser('province', e.target.value)}>
                  <option value="">Select…</option>
                  {PROVINCES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div className="rt-field" style={{ marginTop: 10 }}>
              <label>AIC Designation</label>
              <select value={appraiser.designation ?? ''}
                onChange={e => setAppraiser('designation', e.target.value)}>
                {DESIGNATIONS.map(d => (
                  <option key={d} value={d}>{d || 'Select…'}</option>
                ))}
              </select>
            </div>
          </div>

        </div>
      </div>

      {/* ── CO-SIGNER ── */}
      <div className="rt-section">
        <div
          className="rt-collapsible-header"
          onClick={() => toggleCosigner(!cosignerOpen)}
        >
          <input
            type="checkbox"
            checked={cosigner?.enabled ?? false}
            onChange={e => toggleCosigner(e.target.checked)}
            onClick={e => e.stopPropagation()}
          />
          <span>Add Co-Signer</span>
          <span className="rt-collapsible-arrow">{cosignerOpen ? '▲' : '▼'}</span>
        </div>

        {cosignerOpen && (
          <div className="rt-collapsible-body">
            <div className="rt-row">
              <div className="rt-field rt-field-wide">
                <label>Co-Signer Name</label>
                <input type="text" value={cosigner.name ?? ''}
                  onChange={e => setCosigner('name', e.target.value)} />
              </div>
              <div className="rt-field">
                <label>Designation</label>
                <select value={cosigner.designation ?? ''}
                  onChange={e => setCosigner('designation', e.target.value)}>
                  {DESIGNATIONS.map(d => (
                    <option key={d} value={d}>{d || 'Select…'}</option>
                  ))}
                </select>
              </div>
              <div className="rt-field">
                <label>Membership #</label>
                <input type="text" value={cosigner.membership_number ?? ''}
                  onChange={e => setCosigner('membership_number', e.target.value)} />
              </div>
            </div>

            <div className="rt-row">
              <div className="rt-field">
                <label>Date of Inspection</label>
                <input type="date" value={cosigner.date_inspection || ''}
                  onChange={e => setCosigner('date_inspection', e.target.value)} />
              </div>
              <div className="rt-field">
                <label>Date of Report</label>
                <input type="date" value={cosigner.date_report || ''}
                  onChange={e => setCosigner('date_report', e.target.value)} />
              </div>
            </div>

            <div className="rt-field">
              <label>Professional Assistance</label>
              <select value={cosigner.professional_assistance ?? 'N/A'}
                onChange={e => setCosigner('professional_assistance', e.target.value)}>
                {PROF_ASSIST.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            {cosigner.professional_assistance !== 'N/A' && (
              <div className="rt-field" style={{ marginTop: 10 }}>
                <label>Professional Assistance — Name</label>
                <input type="text" value={cosigner.professional_assistance_text ?? ''}
                  onChange={e => setCosigner('professional_assistance_text', e.target.value)} />
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
